import { useRouter } from 'next/router';
import { Post } from 'lib/types';
import { webAssetUrl } from 'lib/utils/imageUtils';
import { Masonry } from 'components/Masonry';
import { PostListSkeleton } from 'components/PostListSkeleton';
import { Alert, AlertTitle } from '@mui/material';
import { isEmptyProfileQuery } from 'lib/utils/filterUtils';
import { GlobalSearch } from 'components/GlobalSearch';
import { AddToPhotos } from '@mui/icons-material';
import Link from 'next/link';
import { SinglePostResult } from 'features/Profile/SinglePostResult';

interface Props {
  posts: Post[];
  pinnedPosts?: Post[];
  isLoading: boolean;
  isProfileOwner: boolean;
  error?: Error;
  onPinChange: () => void;
  greyOutPosts?: boolean;
  ignoreEmptyState?: boolean;
}

export const FilterPostResult = ({
  posts,
  pinnedPosts = [],
  isLoading,
  isProfileOwner,
  error,
  onPinChange,
  greyOutPosts,
  ignoreEmptyState,
}: Props) => {
  const router = useRouter();

  // If posts are loading and there are no pinned posts yet, show the skeleton
  if (isLoading && !pinnedPosts?.length) {
    return (<div className="mt-4"><PostListSkeleton /></div>);
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        Something went wrong fetching data. Please double check your filters!
      </Alert>
    );
  }

  // If there are no posts after loading, show an empty state
  if (!isLoading && !posts?.length) {
    /* on profile page with no other query */
    if (isEmptyProfileQuery(router.query)) {
      if (ignoreEmptyState) return null;

      if (isProfileOwner) {
        return (
          <div className="w-full h-1/2 flex justify-center">
            <div>
              <div className="text-2xl font-semibold mb-2 text-center">
                Nothing to see here yet
              </div>
              <div className="mb-2 text-center">
                Here are some ways to add your post(s) to your Gondola page:
              </div>
              <div className="p-4 border border-orange rounded-lg mb-4">
                <div className="mb-2">
                  Paste any URL (from any social platform) for something you&apos;ve made
                </div>
                <div>
                  <GlobalSearch label="Try tracking your first post (ex: https://www.instagram.com/p/CusUbIUuYMu)" trackOnly />
                </div>
              </div>
              <div className="text-center mb-4">OR</div>
              <div className="p-4 border border-orange rounded-lg mb-4">
                Click the
                <span className="uppercase text-orange mx-2">
                  <AddToPhotos fontSize="small" className="mr-1" />
                  Add Posts
                </span>
                button above to import multiple posts from any accounts
              </div>
              <div className="text-center mb-4">OR</div>
              <div className="p-4 border border-orange rounded-lg">
                Connect your social media account on the
                <Link href="settings/accounts" className="text-orange underline mx-2">
                  settings page
                </Link>
                to enable automatic importing of posts
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="flex flex-col align-middle items-center justify-center w-full h-1/2 pt-16">
          <div className="text-gray-500 text-3xl font-bold my-4">Just here for the ride</div>
          <img src={webAssetUrl('here-for-the-ride.png')} className="opacity-90" alt="here for the ride" />
        </div>
      );
    }

    return (<div className="text-xl font-semibold text-center">No posts found for the selected filters</div>);
  }

  return (
    <Masonry>
      {pinnedPosts.map((post) => (
        <SinglePostResult
          key={`pin-single-post-${post.id}`}
          post={post}
          isProfileOwner={isProfileOwner}
          onPinChange={onPinChange}
          showPin
          context="profile"
        />
      )).concat(posts.map((post) => (
        <SinglePostResult
          key={`single-post-${post.id}`}
          post={post}
          isProfileOwner={isProfileOwner}
          onPinChange={onPinChange}
          context="profile"
          greyOutPosts={greyOutPosts}
        />
      )))}
    </Masonry>
  );
};
